import { useEffect, useMemo, useState } from 'react';
import { API } from '../../api';
import { useProgramContext } from '../../hooks/useProgramContext';
import { useAppSelector } from '../../redux/hooks';

/**
 * Real setup-guide progress for the admin gamification bar's Setup chip.
 *
 * The completion rules MIRROR SetupGuideModal.isStepDone — keep the two in
 * sync. This hook is READ-ONLY: it never marks a step done (the modal owns
 * that, persisting manual steps to localStorage). Here we only read those
 * signals so the chip reflects the admin's true progress instead of a dummy.
 *
 *   Org admin  — 7 steps: 1 Welcome*, 2 Configure, 3 Integrate, 4 Create,
 *                5 Launch, 6 Manage*, 7 Report*
 *   Group admin — 4-step subset (original steps 1, 5, 6, 7)
 *   (* = manual: completed by proceeding through the modal, stored per-org in
 *    localStorage. The rest derive from org settings / SharePoint / programs.)
 */

export type SetupVariant = 'orgAdmin' | 'groupAdmin';

/**
 * Fired on window by SetupGuideModal right after it stores a manual step. The
 * browser's own `storage` event only reaches OTHER tabs, never the one that
 * wrote, so without this the chip and Elliott's Setup Guide badge kept the old
 * count until a reload.
 */
export const SETUP_PROGRESS_EVENT = 'dosen:setup-progress';

// Local step (1-4) → original step index, for the group-admin subset.
// Mirrors SetupGuideModal.PA_STEP_INDICES.
const PA_STEP_INDICES = [0, 4, 5, 6];

// Optional (original) steps, excluded from the count so progress can reach
// 100% without them. SharePoint (step 3) is an integration many orgs never
// use. Keep in sync with SetupGuideModal.OPTIONAL_ORIGINAL_STEPS.
const OPTIONAL_ORIGINAL_STEPS = new Set<number>([3]);

const getAuthHeaders = () => {
  try {
    const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
    return { idtoken: tokens.id_token || '', authorization: tokens.access_token || '' };
  } catch {
    return { idtoken: '', authorization: '' };
  }
};

export interface AdminSetupProgress {
  done: number;
  total: number;
}

export const useAdminSetupProgress = (variant: SetupVariant): AdminSetupProgress => {
  const { orgId, location, isPlatformAdmin } = useProgramContext();
  const orgSettings = useAppSelector((state: any) => state.orgDetails.data?.orgSettings ?? false);

  const isGroupAdmin = variant === 'groupAdmin';
  const rawTotal = isGroupAdmin ? 4 : 7;
  const manualDoneKey = isGroupAdmin ? `dosen_setup_done_grp_${orgId}` : `dosen_setup_done_${orgId}`;
  const getOriginalStep = (localStep: number) => (isGroupAdmin ? PA_STEP_INDICES[localStep - 1] + 1 : localStep);
  const isRequiredStep = (localStep: number) => !OPTIONAL_ORIGINAL_STEPS.has(getOriginalStep(localStep));
  // Count required steps only, so the chip can reach done === total.
  const total = Array.from({ length: rawTotal }, (_, i) => i + 1).filter(isRequiredStep).length;

  const [manualDone, setManualDone] = useState<Set<number>>(new Set());
  const [isSharePointConnected, setIsSharePointConnected] = useState(false);
  const [programCount, setProgramCount] = useState(0);
  const [hasLaunchedProgram, setHasLaunchedProgram] = useState(false);

  // Manual completions (steps 1/6/7) — written by SetupGuideModal to
  // localStorage. Re-read on SETUP_PROGRESS_EVENT (this tab) and on a storage
  // event (other tabs) so a step the admin just finished in the modal reflects
  // in the chip without a reload.
  useEffect(() => {
    if (!orgId) return;
    const read = () => {
      try {
        const stored = localStorage.getItem(manualDoneKey);
        setManualDone(new Set<number>(stored ? JSON.parse(stored) : []));
      } catch {
        setManualDone(new Set<number>());
      }
    };
    read();
    window.addEventListener('storage', read);
    window.addEventListener(SETUP_PROGRESS_EVENT, read);
    return () => {
      window.removeEventListener('storage', read);
      window.removeEventListener(SETUP_PROGRESS_EVENT, read);
    };
  }, [orgId, manualDoneKey]);

  // API-driven completions (steps 3-5) — same calls SetupGuideModal makes.
  useEffect(() => {
    if (!orgId) return;

    if (!isGroupAdmin) {
      API.getSharePointConnection(orgId)
        .then((data: any) => setIsSharePointConnected(data?.isConnected === true))
        .catch(() => setIsSharePointConnected(false));
    }

    const { idtoken, authorization } = getAuthHeaders();

    // One route for every admin: for a group admin it answers only the programs
    // they administer, which is what their Launch step asks about. (Group admins
    // used to call GET /groups/{orgId}/programs/user, which no longer exists, so
    // their Launch step could never tick.)
    if (idtoken && authorization) {
      API.getV3AdminPrograms({ orgId: isPlatformAdmin ? orgId : undefined, idtoken, authorization, location })
        .then((res: any) => {
          const programs: any[] = Array.isArray(res?.data?.programs) ? res.data.programs : [];
          setProgramCount(programs.length);
          setHasLaunchedProgram(programs.some((p: any) => p.status === 'invited' || p.status === 'active'));
        })
        .catch(() => {
          setProgramCount(0);
          setHasLaunchedProgram(false);
        });
    }
  }, [orgId, isGroupAdmin, isPlatformAdmin, location]);

  const done = useMemo(() => {
    // Mirrors SetupGuideModal.isStepDone(localStep).
    const isStepDone = (localStep: number): boolean => {
      switch (getOriginalStep(localStep)) {
        case 1:
          return manualDone.has(localStep);
        case 2:
          return orgSettings === true;
        case 3:
          return isSharePointConnected;
        case 4:
          return programCount > 0;
        case 5:
          return hasLaunchedProgram;
        case 6:
          return manualDone.has(localStep);
        case 7:
          return manualDone.has(localStep);
        default:
          return false;
      }
    };
    let count = 0;
    for (let n = 1; n <= rawTotal; n++) if (isRequiredStep(n) && isStepDone(n)) count++;
    return count;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualDone, orgSettings, isSharePointConnected, programCount, hasLaunchedProgram, rawTotal, isGroupAdmin]);

  return { done, total };
};
